import { useState } from 'react';
import { PackageCheck } from 'lucide-react';
import { unitTotalsOf, type AwaitingPickupRow, type PipelineReport, type UnitQty, type WarehouseReport } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { cn } from '@eombor/ui';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { requisitionStatusText, unitText } from '../../../shared/i18n/labels.js';
import { AwaitingDetailDialog } from './AwaitingDetailDialog.js';
import { ReportCard } from './ReportCard.js';

/**
 * OLIB KETILMAGAN MAHSULOT — panelning eng qimmat raqami.
 *
 * Nega ALOHIDA karta: bu mahsulot javonda turibdi, boshqa hech kimga
 * berib bo'lmaydi va uni kimdir olib ketishi kerak. Jarayon voronkasi
 * ichidagi qator bo'lib turganda u boshqa bosqichlar bilan bir xil
 * vaznda ko'rinardi va e'tiborga tushmasdi.
 *
 * Son BIRLIKLAR bo'yicha ajratilgan. Aralash yig'indi («235») 220 dona
 * bolt bilan 15 litr moyning qo'shilgani bo'lishi mumkin va bunday
 * sonni na buyurtmaga, na mashinaga o'tkazib bo'ladi.
 *
 * Har satr BOSILADI: ro'yxat «kim kutyapti» degan savolni tug'diradi,
 * keyingisi esa darhol «nega olmagan?» bo'ladi — javob oynada.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

/** «1 240 dona · 85 m» — birliklar nuqta bilan ajratiladi, qo'shilmaydi. */
const UnitTotals = ({ totals, className }: { totals: readonly UnitQty[]; className?: string }) => {
  if (totals.length === 0) return <span className="text-text-faint">—</span>;
  return (
    <span className={cn('flex flex-wrap items-baseline gap-x-2', className)}>
      {totals.map((total, index) => (
        <span key={total.unit} className="flex items-baseline gap-1">
          {index === 0 ? null : <span aria-hidden className="text-text-faint">·</span>}
          <span className="tabular font-semibold">{format(total.qty)}</span>
          <span className="text-sm font-normal text-text-muted">{unitText(total.unit)}</span>
        </span>
      ))}
    </span>
  );
};

/**
 * Kutish MUDDATI qachon ogohlantirishga aylanadi.
 *
 * Chegaralar qat'iy: ularsiz rang qiymatga nisbatan tanlanardi («eng
 * keksasi qizil») va bir kunlik navbatda ham kimdir qizil bo'lib
 * turardi — doim qizil turgan belgi o'qilmay qoladi.
 */
const AGE_WARN_DAYS = 7;
const AGE_DANGER_DAYS = 14;

const ageClass = (days: number): string => {
  if (days >= AGE_DANGER_DAYS) return 'text-danger';
  if (days >= AGE_WARN_DAYS) return 'text-warn';
  return 'text-text-faint';
};

const Row = ({ row, onOpen }: { row: AwaitingPickupRow; onOpen: () => void }) => (
  <li>
    <button
      type="button"
      onClick={onOpen}
      /* Butun satr nishon: kichik havola o'rniga to'liq qator bosiladi —
         sichqoncha bilan ham, barmoq bilan ham tegish oson. */
      className={cn(
        'flex w-full flex-col gap-0.5 rounded-[var(--radius-sm)] px-2 py-2 text-left',
        'transition-colors hover:bg-surface-2 focus-visible:bg-surface-2',
      )}
    >
      <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="tabular shrink-0 text-sm font-medium text-text">{row.docNo}</span>
          <span className="truncate text-xs text-text-faint">
            {requisitionStatusText(row.status)}
          </span>
        </span>
        <span className="flex shrink-0 items-baseline gap-3">
          <UnitTotals totals={unitTotalsOf(row.lines)} className="text-sm text-text" />
          <span className={cn('tabular w-14 text-right text-xs', ageClass(row.waitingDays))}>
            {t('report.pipeAge', { days: row.waitingDays })}
          </span>
        </span>
      </span>

      {/* Kim va qayer — bo'sh maydon CHIZILMAYDI: «—» belgisi qator
          uzunligini ushlab turgani bilan hech narsa aytmaydi. */}
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-text-muted">
        <span className="text-text">{row.requesterName}</span>
        {row.requesterPosition === null ? null : <span>· {row.requesterPosition}</span>}
        {row.orgUnitName === null ? null : <span>· {row.orgUnitName}</span>}
        {row.subdivisionName === null ? null : <span>· {row.subdivisionName}</span>}
        <span className="text-text-faint">· {row.warehouseName}</span>
      </span>
    </button>
  </li>
);

export interface AwaitingPickupBlockProps {
  query: UseQueryResult<PipelineReport, ApiClientError>;
  /**
   * Omborlar kesimi — band qilingan MIQDOR shu yerdan keladi.
   *
   * Miqdor qoldiqdan hisoblanadi va talabnoma kontekstida yo'q, shuning
   * uchun ikki so'rov bitta kartochkani to'ldiradi.
   */
  warehouses: UseQueryResult<WarehouseReport, ApiClientError>;
  onOpenRequisition?: (requisitionId: string) => void;
}

export const AwaitingPickupBlock = ({
  query,
  warehouses,
  onOpenRequisition,
}: AwaitingPickupBlockProps) => {
  const [open, setOpen] = useState<AwaitingPickupRow | null>(null);

  const rows = query.data?.awaiting ?? [];
  const count = query.data?.awaitingPickupCount ?? 0;
  const reserved = warehouses.data?.totals.reservedByUnit ?? [];

  return (
    <ReportCard
      icon={PackageCheck}
      tone="warn"
      title={t('report.awaitTitle')}
      hint={t('report.awaitHint')}
      pending={query.isPending}
      error={query.error ?? null}
      empty={query.isSuccess && count === 0}
      emptyText={t('report.awaitEmpty')}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-4 rounded-[var(--radius-md)] border border-warn/25 bg-warn-soft px-4 py-3.5">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs text-text-muted">{t('report.awaitTotal')}</span>
            {/*
              Miqdor omborlar kesimi so'rovidan keladi va u kechroq
              kelishi mumkin. Shunda chiziqcha turadi — nol bilan
              almashtirilsa, «hech narsa band emas» degan teskari
              xabar chiqardi.
            */}
            <UnitTotals totals={reserved} className="text-2xl leading-8 text-warn" />
          </div>
          <span className="text-sm text-text-muted">
            {t('report.awaitDocs', { count })}
          </span>
        </div>

        <ul className="-mx-2 flex flex-col divide-y divide-border">
          {rows.map((row) => (
            <Row key={row.requisitionId} row={row} onOpen={() => setOpen(row)} />
          ))}
          {count > rows.length ? (
            <li className="px-2 pt-2 text-xs text-text-faint">
              {t('report.pipeMore', { count: count - rows.length })}
            </li>
          ) : null}
        </ul>
      </div>

      <AwaitingDetailDialog
        row={open}
        onClose={() => setOpen(null)}
        {...(onOpenRequisition === undefined ? {} : { onOpenRequisition })}
      />
    </ReportCard>
  );
};
